import { useState } from "react";
import { Platform } from "react-native";
import { HLS_STREAMING } from "src/api/featureFlags";
import parseV0TemplateUrl from "src/helpers/parseV0TemplateUrl";
import useClearingTimeouts from "src/hooks/useClearingTimeouts";
import { Image } from "src/swsh-native";
import { PhotoDisplayContentProps } from "../../types";
import PhotoDisplayLivePhotoContentContent from "./components/PhotoDisplayLivePhotoContentContent";

const PhotoDisplayLivePhotoContent = ({
	photo,
	photoWidth,
	photoHeight,
	onLoad,
	onDetailedLoad,
	isActive,
}: PhotoDisplayContentProps) => {
	const [renderPreview, setRenderPreview] = useState(true);
	const { createClearingTimeout } = useClearingTimeouts();
	const imageUrl =
		parseV0TemplateUrl({
			readUrl: photo.readUrl,
			strategy: "inside",
			size: "lg",
		}) ?? photo.readUrl?.stableUrl;
	const upgradedImageUrl =
		parseV0TemplateUrl({
			readUrl: photo.readUrl,
			strategy: "inside",
			size: "xxl",
		}) ??
		// Keep stableUrl for optimistic behaviors
		photo.readUrl?.stableUrl;

	const videoUrl =
		(HLS_STREAMING
			? Platform.select({
					// Preferring original video for iOS -- iOS should be able to handle whatever live photo video format
					ios: parseV0TemplateUrl({
						readUrl: photo.readUrl,
						direct: "original-video",
					}),
					android: parseV0TemplateUrl({
						readUrl: photo.readUrl,
						auto: "allowStream",
					}),
					// PhotoDisplayLivePhotoContentContent has custom logic to handle streams
					web: parseV0TemplateUrl({
						readUrl: photo.readUrl,
						auto: "allowStream",
					}),
				})
			: parseV0TemplateUrl({
					readUrl: photo.readUrl,
					direct: "stable-video",
				})) ??
		// Keep stableVideoUrl for optimistic behaviors
		photo.readUrl?.stableVideoUrl;
	if (!imageUrl || !upgradedImageUrl || !videoUrl) {
		return null;
	}
	return (
		<>
			{renderPreview && (
				<Image
					src={imageUrl}
					alt="Live Photo Preview"
					dim={{
						width: photoWidth,
						height: photoHeight,
					}}
					blurHash={photo.blurHash ?? undefined}
					style={{
						position: "absolute",
						zIndex: -1,
					}}
				/>
			)}
			{isActive && (
				<PhotoDisplayLivePhotoContentContent
					onPreviewLoadEnd={({ success }) => {
						onLoad?.({
							success,
						});
						createClearingTimeout(() => {
							setRenderPreview(false);
						}, 1000);
					}}
					onVideoLoadEnd={() => {
						onDetailedLoad?.({
							success: true,
						});
					}}
					style={{
						width: photoWidth,
						height: photoHeight,
					}}
					source={{
						imageUri: upgradedImageUrl,
						videoUri: videoUrl,
						h264VideoUri: videoUrl,
					}}
				/>
			)}
		</>
	);
};
export default PhotoDisplayLivePhotoContent;
